/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import { XError, ActionRequest, ActionSuccess, ActionFail, User, PinErrors, Ducks } from '@wit/vtv-x-foundation-lib';
import { SagaIterator } from 'redux-saga';
import { put, race, select, take, takeLeading } from 'redux-saga/effects';
import * as ParentalControl from 'core/store/ducks/parentalControl';
import { StoreState } from '../../store';

export enum ActionTypes {
  PIN_RESET = 'ui/managePin/PIN_RESET',
  PIN_VALIDATE = 'ui/managePin/PIN_VALIDATE',
  PIN_SUCCESS = 'ui/managePin/PIN_SUCCESS',
  PIN_FAIL = 'ui/managePin/PIN_FAIL',
  PIN_ERROR = 'ui/managePin/PIN_ERROR',
  PIN_VALIDATE_BOTH_REQUEST = 'ui/managePin/PIN_VALIDATE_BOTH_REQUEST',
  PIN_VALIDATE_BOTH_SUCCESS = 'ui/managePin/PIN_VALIDATE_BOTH_SUCCESS',
  PIN_VALIDATE_BOTH_ERROR = 'ui/managePin/PIN_VALIDATE_BOTH_ERROR',
}

interface PinResponsePayload {
  isRetry?: boolean;
  newPin?: string;
  confirmNewPin?: string;
  isMissmatch?: boolean;
}
type PinReset = ActionRequest<ActionTypes.PIN_RESET>;
type PinSuccess = ActionSuccess<ActionTypes.PIN_SUCCESS>;
type PinValidateBothRequest = ActionRequest<ActionTypes.PIN_VALIDATE_BOTH_REQUEST, PinResponsePayload>;
type PinValidateBothSuccess = ActionSuccess<ActionTypes.PIN_VALIDATE_BOTH_SUCCESS>;
type PinValidateBothError = ActionRequest<ActionTypes.PIN_VALIDATE_BOTH_ERROR, PinResponsePayload>;
type PinError = ActionFail<ActionTypes.PIN_ERROR>;
type PinValidate = ActionRequest<ActionTypes.PIN_VALIDATE, string>;

type Actions =
  | PinReset
  | PinSuccess
  | PinError
  | PinValidate
  | PinValidateBothRequest
  | PinValidateBothSuccess
  | PinValidateBothError;

const Actions = {
  pinReset: (): PinReset => ({ type: ActionTypes.PIN_RESET }),
  pinValidate: (pinValidate: string): PinValidate => ({
    type: ActionTypes.PIN_VALIDATE,
    meta: pinValidate,
  }),
  pinSucess: (): PinSuccess => ({
    type: ActionTypes.PIN_SUCCESS,
  }),
  pinValidateBothRequest: (pinRetryPayload: PinResponsePayload): PinValidateBothRequest => ({
    type: ActionTypes.PIN_VALIDATE_BOTH_REQUEST,
    meta: pinRetryPayload,
  }),
  pinValidateBothSuccess: (): PinValidateBothSuccess => ({
    type: ActionTypes.PIN_VALIDATE_BOTH_SUCCESS,
  }),
  pinValidateBothError: (isMissmatch: PinResponsePayload): PinValidateBothError => ({
    type: ActionTypes.PIN_VALIDATE_BOTH_ERROR,
    meta: isMissmatch,
  }),
  pinError: (error: XError): PinError => ({
    type: ActionTypes.PIN_ERROR,
    error,
  }),
};

export const ActionCreators = {
  pinReset: Actions.pinReset,
  pinMissmatch: Actions.pinValidateBothError,
  pinValidateBoth: Actions.pinValidateBothRequest,
  pinValidate: Actions.pinValidate,
  pinError: Actions.pinError,
};

// REDUCER
export interface ReducerState {
  error: XError | undefined;
  processing: boolean;
  processed: boolean;
  pinIsCorrect: boolean | undefined;
  isMissmatch: boolean | undefined;
  pinChangedSuccess: boolean | undefined;
}

const initialState: ReducerState = {
  pinIsCorrect: undefined,
  error: undefined,
  processed: false,
  processing: false,
  isMissmatch: false,
  pinChangedSuccess: false,
};

export default function pin(state = initialState, action: Actions): ReducerState {
  switch (action.type) {
    case ActionTypes.PIN_RESET:
      return initialState;
    case ActionTypes.PIN_VALIDATE:
      return {
        ...state,
        pinIsCorrect: undefined,
        processing: true,
        processed: false,
        error: undefined,
      };
    case ActionTypes.PIN_SUCCESS:
      return {
        ...state,
        pinIsCorrect: true,
        processing: false,
        processed: true,
        error: undefined,
      };
    case ActionTypes.PIN_VALIDATE_BOTH_REQUEST:
      return {
        ...state,
        error: undefined,
        isMissmatch: false,
        processed: false,
        processing: true,
      };
    case ActionTypes.PIN_VALIDATE_BOTH_SUCCESS:
      return {
        ...state,
        pinChangedSuccess: true,
        processed: true,
        processing: false,
      };
    case ActionTypes.PIN_VALIDATE_BOTH_ERROR:
      return {
        ...state,
        isMissmatch: action.meta.isMissmatch,
        processed: true,
        processing: false,
      };
    case ActionTypes.PIN_ERROR:
      return {
        ...state,
        processed: true,
        processing: false,
        pinIsCorrect: undefined,
        error: action.error,
      };

    default:
      return state;
  }
}

// SELECTORS
export const Selectors = {
  isProcessed: (state: StoreState): boolean => state.settings.managePin.processed === true,
  isProcessing: (state: StoreState): boolean => state.settings.managePin.processing === true,
  getError: (state: StoreState): XError | undefined => state.settings.managePin.error,
  getPinIsCorrect: (state: StoreState): boolean | undefined => state.settings.managePin.pinIsCorrect,
  getIsMissmatch: (state: StoreState): boolean | undefined => state.settings.managePin.isMissmatch,
  pinChangedSuccess: (state: StoreState): boolean | undefined => state.settings.managePin.pinChangedSuccess,
};

function* validatePin(action: PinValidate): SagaIterator {
  try {
    // Get User preferences
    const user: User = yield select(Ducks.Household.Selectors.data.getDefaultUser);

    yield put(Ducks.Household.Creators.getUser(user));

    const { requestUserSuccess, requestUserFail } = yield race({
      requestUserSuccess: take(Ducks.Household.ActionType.getUser.SUCCESS),
      requestUserFail: take(Ducks.Household.ActionType.getUser.FAIL),
    });

    if (requestUserSuccess) {
      const { payload: userUpdated } = requestUserSuccess;
      if (action.meta === userUpdated.masterPin) {
        yield put(Actions.pinSucess());
      } else {
        yield put(Actions.pinError(XError.fromCode(PinErrors.validation.inputIncorrectPIN)));
      }
    } else if (requestUserFail) {
      yield put(Actions.pinError(XError.fromError(requestUserFail.error)));
    } else {
      throw new Error('Something went wrong retrieving user details for PIN validation.');
    }
  } catch (error) {
    yield put(Actions.pinError(XError.fromError(error)));
  }
}

function* verifyBothPins(action: PinValidateBothRequest): SagaIterator {
  const { meta } = action;
  const { newPin, confirmNewPin } = meta;

  try {
    if (newPin === confirmNewPin) {
      yield put(Actions.pinValidateBothSuccess());
      if (newPin) {
        yield put(ParentalControl.ActionCreators.changeMasterPin(newPin));
      }
    } else {
      yield put(Actions.pinValidateBothError({ isMissmatch: true }));
    }
  } catch (err) {
    yield put(Actions.pinError(XError.fromError(err)));
  }
}

export function* saga(): SagaIterator {
  yield takeLeading(ActionTypes.PIN_VALIDATE_BOTH_REQUEST, verifyBothPins);
  yield takeLeading(ActionTypes.PIN_VALIDATE, validatePin);
}
